import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../utils/Api";
import SEO from "../components/SEO";

function getPrimaryImage(product) {
  const image = product.image_url;

  if (!image) {
    return "/placeholder-product.jpg";
  }

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  return `https://kinewsstudiolimited.co.uk/${image.replace(/^\/+/, "")}`;
}

export default function Shop() {
  const { apiFetch } = useApi();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    let mounted = true;

    const loadShopData = async () => {
      try {
        setLoading(true);
        setLoadError("");

        const [productsRes, categoriesRes] = await Promise.all([
          apiFetch("list_public_shop_products"),
          apiFetch("list_public_shop_categories"),
        ]);

        if (!productsRes?.success) {
          throw new Error(productsRes?.message || "Failed to load products.");
        }

        if (!categoriesRes?.success) {
          throw new Error(
            categoriesRes?.message || "Failed to load categories.",
          );
        }

        if (!mounted) return;

        setProducts(
          Array.isArray(productsRes.data)
            ? productsRes.data.filter((item) => Number(item.is_active) === 1)
            : [],
        );

        setCategories(
          Array.isArray(categoriesRes.data)
            ? categoriesRes.data.filter((item) => Number(item.is_active) === 1)
            : [],
        );
      } catch (err) {
        if (!mounted) return;
        setLoadError(err.message || "Unable to load shop data.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadShopData();

    return () => {
      mounted = false;
    };
  }, [apiFetch]);

  const categoryTabs = useMemo(() => {
    return [
      { id: "All", name: "All" },
      ...categories.map((category) => ({
        id: String(category.id),
        name: category.name,
      })),
    ];
  }, [categories]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();

    return products.filter((product) => {
      const matchSearch =
        !s ||
        product.name?.toLowerCase().includes(s) ||
        (product.details || "").toLowerCase().includes(s);

      const matchCategory =
        activeCategory === "All" ||
        String(product.category_id) === String(activeCategory);

      return matchSearch && matchCategory;
    });
  }, [products, search, activeCategory]);

  return (
    <>
      <SEO
        title="Frames, Prints & Personalised Gifts | Kinews Studio Limited"
        description="Shop high-quality photo frames, prints and personalised gifts including mugs, t-shirts and more at Kinews Studio Limited."
        url={window.location.href}
      />
      <main className="min-h-screen bg-white text-black">
        <section className="relative overflow-hidden bg-[#050505] text-white">
          {/* Lighting Effects */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-[10%] -top-[10%] h-[40rem] w-[40rem] rounded-full bg-pink-500/15 blur-[120px]" />
            <div className="absolute -right-[10%] top-[20%] h-[35rem] w-[35rem] rounded-full bg-pink-600/10 blur-[100px]" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[50rem] w-[50rem] rounded-full bg-pink-500/5 blur-[150px]" />
          </div>

          <div className="relative mx-auto max-w-7xl px-5 pb-14 pt-28 md:pb-24 md:pt-40">
            <h1 className="mt-3 text-4xl font-bold tracking-tighter md:text-5xl lg:text-6xl">
              Shop
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/60 md:text-xl">
              Browse custom items and order directly online.
            </p>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-5 py-12">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <h2 className="text-2xl font-semibold md:text-3xl">Products</h2>
                <p className="mt-2 text-sm text-black/60">
                  Browse products by category.
                </p>
              </div>

              <div className="w-full md:w-auto">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products..."
                  className="w-full rounded-2xl border border-black/15 bg-white px-4 py-3 text-sm placeholder:text-black/35 outline-none focus:border-black/35 md:w-[320px]"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {categoryTabs.map((tab) => {
                const isActive = activeCategory === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveCategory(tab.id)}
                    className={[
                      "rounded-2xl border px-4 py-2 text-sm font-semibold transition",
                      isActive
                        ? "border-black bg-black text-white"
                        : "border-black/15 bg-white text-black hover:border-black/35",
                    ].join(" ")}
                  >
                    {tab.name}
                  </button>
                );
              })}
            </div>

            {loading ? (
              <div className="mt-10 rounded-3xl border border-black/10 bg-black/[0.03] p-8">
                <p className="font-semibold">Loading products...</p>
              </div>
            ) : loadError ? (
              <div className="mt-10 rounded-3xl border border-red-200 bg-red-50 p-8">
                <p className="font-semibold text-red-700">{loadError}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="mt-10 rounded-3xl border border-black/10 bg-black/[0.03] p-8">
                <p className="font-semibold">No products found.</p>
                <p className="mt-2 text-sm text-black/60">
                  Try another category or keyword.
                </p>
              </div>
            ) : (
              <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((product) => {
                  const image = getPrimaryImage(product);

                  return (
                    <div
                      key={product.id}
                      onClick={() => navigate(`/shop/${product.id}`)}
                      className="group cursor-pointer overflow-hidden rounded-3xl border border-black/10 bg-white transition duration-200 hover:-translate-y-1 hover:border-black/30 hover:shadow-lg"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden bg-black/[0.04]">
                        <img
                          src={image}
                          alt={product.name}
                          className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>

                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-slate-900">
                          {product.name}
                        </h3>

                        <p className="mt-1 text-xs text-slate-500">
                          From:{" "}
                          <span className="text-sm font-medium text-slate-900">
                            {product.base_price !== null
                              ? `£${Number(product.base_price).toFixed(2)}`
                              : "£0.00"}
                          </span>
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
